/**
 * Troca de senha da própria conta (P01-E).
 *
 * O usuário autenticado altera a própria senha pelo cliente Supabase
 * (`auth.updateUser`), nunca por `service_role`. Aqui ficam apenas as regras
 * e as mensagens em português — sem I/O — para poder testar sem rede.
 */

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordChangeInput {
  current: string;
  next: string;
  confirm: string;
}

export interface PasswordChangeErrors {
  current?: string;
  next?: string;
  confirm?: string;
}

export interface PasswordChangeValidation {
  ok: boolean;
  errors: PasswordChangeErrors;
}

export function validatePasswordChange(input: PasswordChangeInput): PasswordChangeValidation {
  const errors: PasswordChangeErrors = {};

  if (!input.current) {
    errors.current = "Informe a senha atual.";
  }

  if (!input.next) {
    errors.next = "Informe a nova senha.";
  } else if (input.next.length < PASSWORD_MIN_LENGTH) {
    errors.next = `A nova senha precisa ter ao menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  } else if (input.next === input.current) {
    errors.next = "A nova senha deve ser diferente da atual.";
  }

  if (!input.confirm) {
    errors.confirm = "Repita a nova senha.";
  } else if (input.next && input.confirm !== input.next) {
    errors.confirm = "As senhas não conferem.";
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

export function translatePasswordError(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes("invalid login credentials")) return "Senha atual incorreta.";
  if (normalized.includes("different from the old password")) {
    return "A nova senha deve ser diferente da atual.";
  }
  if (normalized.includes("at least") || normalized.includes("password should be")) {
    return `A nova senha precisa ter ao menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (normalized.includes("weak") || normalized.includes("pwned")) {
    return "Senha considerada fraca ou já vazada. Escolha outra.";
  }
  if (normalized.includes("same password")) return "A nova senha deve ser diferente da atual.";
  if (normalized.includes("too many requests") || normalized.includes("rate limit")) {
    return "Muitas tentativas. Tente novamente em instantes.";
  }
  if (normalized.includes("failed to fetch") || normalized.includes("network")) {
    return "Falha de conexão com o servidor.";
  }
  return "Não foi possível alterar a senha. Tente novamente.";
}
